import React, {useContext, useState} from "react";
import CodeInput from "../../components/codeInput/CodeInput";
import StyleContext from "../../contexts/StyleContext";
import {usePrivateAuth} from "../../hooks/usePrivateAuth";
import DocumentViewer from "./DocumentViewer";
import "./PrivateSpace.scss";

export default function PrivateSpace() {
  const {isDark} = useContext(StyleContext);
  const {
    status,
    content,
    collections,
    isSyncing,
    error,
    isSubmitting,
    login,
    logout
  } = usePrivateAuth();
  // Bumped on every failed attempt to tell CodeInput to clear itself.
  const [resetSignal, setResetSignal] = useState(0);
  const [openDocument, setOpenDocument] = useState(null);

  const handleComplete = async code => {
    const succeeded = await login(code);
    if (!succeeded) setResetSignal(signal => signal + 1);
  };

  const handleLogout = () => {
    setOpenDocument(null);
    logout();
  };

  if (status === "checking") {
    return (
      <div className="private-space-main">
        <div className="private-space-container">
          <p className="private-checking">Checking your session…</p>
        </div>
      </div>
    );
  }

  if (status === "unlocked" && openDocument) {
    return (
      <DocumentViewer
        document={openDocument}
        isDark={isDark}
        onClose={() => setOpenDocument(null)}
      />
    );
  }

  return (
    <div className="private-space-main" id="private">
      <div className="private-space-container">
        {status === "unlocked" && content ? (
          <UnlockedView
            content={content}
            collections={collections}
            isSyncing={isSyncing}
            isDark={isDark}
            onOpenDocument={setOpenDocument}
            onLogout={handleLogout}
          />
        ) : (
          <LockedView
            isDark={isDark}
            error={error}
            isSubmitting={isSubmitting}
            resetSignal={resetSignal}
            onComplete={handleComplete}
          />
        )}
      </div>
    </div>
  );
}

function LockedView({isDark, error, isSubmitting, resetSignal, onComplete}) {
  return (
    <div className={isDark ? "private-card dark-mode" : "private-card"}>
      <div className="private-lock-badge" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zm0 2a3 3 0 0 1 3 3v3H9V6a3 3 0 0 1 3-3zm0 11a1.75 1.75 0 0 1 1 3.19V19a1 1 0 0 1-2 0v-1.81A1.75 1.75 0 0 1 12 14z" />
        </svg>
      </div>

      <h1 className={isDark ? "private-title dark-mode" : "private-title"}>
        Private Space
      </h1>
      <p className="private-subtitle subTitle">
        Enter your 6-digit access code to continue.
      </p>

      <CodeInput
        isDark={isDark}
        disabled={isSubmitting}
        resetSignal={resetSignal}
        onComplete={onComplete}
      />

      <div className="private-status" aria-live="polite">
        {isSubmitting && <span className="private-checking">Verifying…</span>}
        {!isSubmitting && error && (
          <span className="private-error">{error}</span>
        )}
      </div>
    </div>
  );
}

function UnlockedView({
  content,
  collections,
  isSyncing,
  isDark,
  onOpenDocument,
  onLogout
}) {
  return (
    <div className={isDark ? "private-card dark-mode" : "private-card"}>
      <div className="private-unlocked-header">
        <div>
          <h1 className={isDark ? "private-title dark-mode" : "private-title"}>
            {content.title || "Private Space"}
          </h1>
          {content.subtitle && (
            <p className="private-subtitle subTitle">{content.subtitle}</p>
          )}
        </div>
        <button type="button" className="private-logout" onClick={onLogout}>
          Log out
        </button>
      </div>

      {collections.length > 0 && (
        <p className="private-sync-note subTitle">
          {isSyncing ? (
            "Your progress is saved to your account and follows you to any device you log in from."
          ) : (
            <span className="private-sync-warning">
              Progress is saved in this browser only — cross-device syncing is
              not configured on the server yet.
            </span>
          )}
        </p>
      )}

      {collections.map(collection => (
        <section
          key={collection.id}
          className={isDark ? "private-section dark-mode" : "private-section"}
        >
          <h2 className="private-section-heading">{collection.label}</h2>
          <p className="private-section-description subTitle">
            {collection.documents.length} study guide
            {collection.documents.length === 1 ? "" : "s"}.
          </p>
          <div className="private-doc-grid">
            {collection.documents.map(document => (
              <button
                key={document.file}
                type="button"
                className={
                  isDark ? "private-doc-card dark-mode" : "private-doc-card"
                }
                onClick={() =>
                  onOpenDocument({...document, collection: collection.id})
                }
              >
                <span className="private-doc-title">{document.title}</span>
                <span className="private-doc-meta">
                  {document.file} · {document.sizeKb} KB
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
